/* This adapter owns and tears down mutable browser DOM/window resources. */
/* eslint-disable @silverhand/fp/no-mutation, @silverhand/fp/no-let */
import type { WechatAuthorization } from './ports';
import {
  callbackAddress,
  callbackMessage,
  identityOrigin,
  officialAuthorization,
  officialFrameURL,
  opaque,
  readWechatReference,
  referenceKey,
} from './protocol';

/** The callback page can only notify its immediate, same-identity owning frame. */
export function notifyWechatCallbackParent(connectorId: string): boolean {
  if (
    !opaque(connectorId) ||
    window.location.origin !== identityOrigin ||
    window.parent === window
  ) {
    return false;
  }
  try {
    const frame = window.frameElement;
    if (
      frame?.tagName !== 'IFRAME' ||
      frame.namespaceURI !== 'http://www.w3.org/1999/xhtml' ||
      // The child sees an Element from another realm; instanceof and dataset narrowing are unreliable.
      // eslint-disable-next-line unicorn/prefer-dom-node-dataset
      frame.getAttribute('data-vibehub-wechat') !== connectorId ||
      window.parent.location.origin !== identityOrigin
    ) {
      return false;
    }
    window.parent.postMessage({ type: callbackMessage }, identityOrigin);
    return true;
  } catch {
    return false;
  }
}

/** Messages carry a hint only. The original callback validates state and code. */
export function mountOfficialWechatQR(
  container: HTMLElement,
  value: WechatAuthorization,
  onUnavailable: () => void
): () => void {
  const address = officialFrameURL(value.authorization, value.reference);
  if (!address || window.location.origin !== identityOrigin) {
    throw new Error('WECHAT_QR_UNAVAILABLE');
  }
  const frame = document.createElement('iframe');
  frame.title = '微信官方扫码登录';
  frame.dataset.vibehubWechat = value.reference.connectorId;
  frame.width = '300';
  frame.height = '400';
  frame.scrolling = 'no';
  frame.referrerPolicy = 'no-referrer';
  frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
  frame.src = address;
  let active = true;
  const receive = (event: MessageEvent<unknown>) => {
    if (
      !active ||
      !frame.isConnected ||
      event.origin !== identityOrigin ||
      event.source !== frame.contentWindow ||
      !event.data ||
      typeof event.data !== 'object' ||
      Object.keys(event.data).join(',') !== 'type' ||
      !('type' in event.data) ||
      event.data.type !== callbackMessage
    ) {
      return;
    }
    try {
      const target = callbackAddress(frame.contentWindow?.location.href ?? '', value.reference);
      if (!target) {
        onUnavailable();
        return;
      }
      // Navigate this identity page, never the cross-origin product host.
      window.location.replace(target);
    } catch {
      onUnavailable();
    }
  };
  const failed = () => {
    if (active) {
      onUnavailable();
    }
  };
  frame.addEventListener('error', failed);
  window.addEventListener('message', receive);
  container.replaceChildren(frame);
  return () => {
    active = false;
    window.removeEventListener('message', receive);
    frame.removeEventListener('error', failed);
    frame.remove();
  };
}

export function openOfficialWechatWindow(value: WechatAuthorization): boolean {
  const address = officialAuthorization(
    value.authorization,
    value.reference.connectorId,
    value.reference.state
  );
  if (!address || window.location.origin !== identityOrigin) {
    return false;
  }
  // A user gesture opens a same-origin blank window first, retaining the
  // original browser OAuth session references before removing its opener.
  const child = window.open('about:blank', '_blank');
  if (!child) {
    return false;
  }
  try {
    child.opener = null;
    const reference = readWechatReference(window.sessionStorage.getItem(referenceKey));
    if (
      !reference ||
      reference.state !== value.reference.state ||
      reference.connectorId !== value.reference.connectorId ||
      reference.verificationId !== value.reference.verificationId ||
      reference.flow !== value.reference.flow
    ) {
      child.close();
      return false;
    }
    child.sessionStorage.setItem(referenceKey, JSON.stringify(reference));
    child.location.replace(address);
    return true;
  } catch {
    child.close();
    return false;
  }
}

/* eslint-enable @silverhand/fp/no-mutation, @silverhand/fp/no-let */
